/**
 * تنظیماتِ اعلان — قرینه‌ی apps/mobile/app/notification-settings.tsx.
 * برای هر دسته‌ی خبر: چطور بهم گفته شود — پوش / فقط داخل اپ / هیچی.
 */
import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DEFAULT_PREFS, notifPrefsRepo } from '@/core/data/notifPrefsRepo';
import { useSession } from '@/core/session';
import { t, useLocale } from '@/core/lib/i18n';
import { reportError } from '@/core/lib/report';
import { Card, Help } from '@/ui/kit';
import { PushOfferCard } from '@/features/notifications/PushOfferCard';
import { PushManageCard } from '@/features/notifications/PushManageCard';
import type { NotifCategory, NotifMode, NotifPrefs, TagFollowMode } from '@/core/domain/types';

const CATEGORIES: { key: NotifCategory; title: string; hint: string }[] = [
  { key: 'friend_request', title: 'notifCatFriendRequest', hint: 'notifCatFriendRequestHint' },
  { key: 'signal', title: 'notifCatSignal', hint: 'notifCatSignalHint' },
  { key: 'event', title: 'notifCatEvent', hint: 'notifCatEventHint' },
];

const MODES: { key: NotifMode; label: string; hint: string }[] = [
  { key: 'push', label: 'notifModePush', hint: 'notifModePushHint' },
  { key: 'inapp', label: 'notifModeInApp', hint: 'notifModeInAppHint' },
  { key: 'off', label: 'notifModeOff', hint: 'notifModeOffHint' },
];

// تگ‌های دنبال‌شده (مایگریشن 0094) تنها دسته‌ایه که «ایمیل» هم داره؛ برای
// همین توی CATEGORIES/MODES بالا قاطی نشده و فهرستِ مُدهای خودش رو داره.
const TAG_MODES: { key: TagFollowMode; label: string; hint: string }[] = [
  { key: 'push', label: 'notifModePush', hint: 'notifModePushHint' },
  { key: 'email', label: 'notifModeEmail', hint: 'notifModeEmailHint' },
  { key: 'inapp', label: 'notifModeInApp', hint: 'notifModeInAppHint' },
  { key: 'off', label: 'notifModeOff', hint: 'notifModeOffHint' },
];

export function NotificationSettingsPage() {
  useLocale();
  const { profile } = useSession();
  const qc = useQueryClient();

  const prefs = useQuery({
    queryKey: ['notifPrefs'],
    queryFn: notifPrefsRepo.mine,
    enabled: !!profile,
  });

  const patch = useMutation({
    mutationFn: (p: Partial<NotifPrefs>) => notifPrefsRepo.patch(profile!.id, p),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifPrefs'] }),
    onError: (e) => reportError('web.notifPrefs.patch', e),
  });

  const current = prefs.data ?? DEFAULT_PREFS;

  return (
    <div className="feed">
      <h1 className="feed-title">{t('notifSettings')}</h1>

      {/* بالای همه‌ی تنظیم‌ها، چون تا اجازه‌ی مرورگر نباشد گزینه‌ی
          «اعلان» در جدولِ پایین روی وب کارِ «فقط داخل اپ» را می‌کند.
          خودش تصمیم می‌گیرد که اصلاً دیده شود یا نه. `deliberate` چون کسی
          که تا این‌جا آمده خودش دنبالِ اعلان است — شرطِ «اولین فعالیت»
          (WHOSUP-71) فقط مالِ پیشنهادِ سرزده در فید است. */}
      <PushOfferCard deliberate />
      {/* و وقتی اجازه از قبل هست: وضعیت + خاموش/روشن. حذفِ واقعیِ توکن،
          نه بی‌صدایی — نگاه کن به PushManageCard. */}
      <PushManageCard />

      {prefs.isLoading ? <p className="dim">…</p> : (
        <>
          {CATEGORIES.map((c) => (
            <Card key={c.key}>
              {/* توضیحِ هر دسته پشتِ «؟» کنارِ عنوانش. */}
              <h3>{t(c.title)}<Help text={t(c.hint)} /></h3>
              {MODES.map((m) => (
                <label key={m.key} className="radio-row">
                  <input type="radio" name={c.key} checked={current[c.key] === m.key}
                    onChange={() => patch.mutate({ [c.key]: m.key } as Partial<NotifPrefs>)} />
                  <span>
                    <b>{t(m.label)}</b>
                    <small>{t(m.hint)}</small>
                  </span>
                </label>
              ))}
            </Card>
          ))}

          <Card>
            <h3>{t('notifCatTagFollow')}<Help text={t('notifCatTagFollowHint')} /></h3>
            {TAG_MODES.map((m) => (
              <label key={m.key} className="radio-row">
                <input type="radio" name="tag_follow" checked={current.tag_follow === m.key}
                  onChange={() => patch.mutate({ tag_follow: m.key })} />
                <span>
                  <b>{t(m.label)}</b>
                  <small>{t(m.hint)}</small>
                </span>
              </label>
            ))}
          </Card>

          <Card>
            {/* «بعضی بروزرسانی‌ها نیتیوند و لینک می‌فرستیم» پشتِ «؟». */}
            <h3>{t('updateTitle')}<Help text={t('updateNativeNote')} /></h3>
            <label className="radio-row">
              <input type="radio" name="auto_update" checked={current.auto_update}
                onChange={() => patch.mutate({ auto_update: true })} />
              <span><b>{t('updateAuto')}</b><small>{t('updateAutoHint')}</small></span>
            </label>
            <label className="radio-row">
              <input type="radio" name="auto_update" checked={!current.auto_update}
                onChange={() => patch.mutate({ auto_update: false })} />
              <span><b>{t('updateManual')}</b><small>{t('updateManualHint')}</small></span>
            </label>
          </Card>
        </>
      )}
    </div>
  );
}
